
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil, Check, X } from 'lucide-react';
import { useBoardContext } from '@/contexts/BoardContext';
import type { Tables } from '@/integrations/supabase/types';

type Column = Tables<'columns'>;

interface ColumnRenameProps {
  column: Column;
}

export const ColumnRename = ({ column }: ColumnRenameProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(column.title);
  const { updateColumn } = useBoardContext();

  const handleSave = () => {
    if (newTitle.trim() && newTitle !== column.title) {
      updateColumn({ columnId: column.id, title: newTitle.trim() });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setNewTitle(column.title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 flex-1">
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-6 text-sm font-semibold"
          autoFocus
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSave}
          className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
        >
          <Check className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-1">
      <h3 className="font-semibold text-gray-800 dark:text-slate-200">{column.title}</h3>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsEditing(true)}
        className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <Pencil className="h-3 w-3" />
      </Button>
    </div>
  );
};
